import React, { useEffect, useState, useCallback } from "react";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import { Card, Row, Col, Button, Dropdown } from "react-bootstrap";
import { Link } from "react-router-dom";
import { getToken, getUserId } from "../services/authorize";
import axiosInstance from "../services/axiosInterceptor";
import { useLoading } from "../contexts/LoadingContext";
import { getCurrentSemester } from "../utils/teachingUtils";
import jsPDF from "jspdf";

const API = process.env.REACT_APP_API;
const DAYS = [0, 1, 2, 3, 4, 5, 6];
const DAY_NAMES = { 0: "อาทิตย์", 1: "จันทร์", 2: "อังคาร", 3: "พุธ", 4: "พฤหัส", 5: "ศุกร์", 6: "เสาร์" };

const SEMESTER_COLORS = {
  "1": { bg: "#e3f2fd", border: "#1976d2", text: "#0d47a1" },
  "2": { bg: "#fce4ec", border: "#c62828", text: "#b71c1c" },
};

export default function MyTimetable() {
  const currentSkin = localStorage.getItem("skin-mode") ? "dark" : "";
  const [skin, setSkin] = useState(currentSkin);

  const switchSkin = (s) => {
    const tables = document.getElementsByTagName("table");
    for (const table of tables) {
      const theads = table.querySelectorAll("thead");
      if (s === "dark") {
        for (const thead of theads) { thead.classList.remove("table-secondary"); thead.classList.add("table-primary"); }
        table.classList.add("table-dark");
      } else {
        table.classList.remove("table-dark");
        for (const thead of theads) { thead.classList.remove("table-primary"); thead.classList.add("table-secondary"); }
      }
    }
  };
  switchSkin(skin);
  useEffect(() => { switchSkin(skin); }, [skin]);

  const { setLoading } = useLoading();

  const [yearList, setYearList] = useState([]);
  const [yearId, setYearId] = useState(() => localStorage.getItem("yearId") || null);
  const [yearLabel, setYearLabel] = useState("");
  const [periods, setPeriods] = useState([]);
  const [teachingData, setTeachingData] = useState([]);
  const [guidanceData, setGuidanceData] = useState([]); // คาบแนะแนวของห้องที่เป็นครูประจำชั้น
  const [semester, setSemester] = useState(getCurrentSemester()); // เริ่มที่ภาคเรียนปัจจุบัน
  const [teacherName, setTeacherName] = useState("");

  const authHeader = useCallback(() => ({
    headers: { authorization: `Bearer ${getToken()}` },
  }), []);

  // ─── Load year list ───────────────────────────────────────────────────────
  useEffect(() => {
    axiosInstance.get(`${API}/getStudyYear`, authHeader()).then((res) => {
      const list = res.data;
      setYearList(list || []);
      let id = localStorage.getItem("yearId");
      if (!id && list.length > 0) { id = list[0].year_id; localStorage.setItem("yearId", id); }
      setYearId(id);
      const sel = list.find((y) => String(y.year_id) === String(id));
      setYearLabel(sel ? `ปีการศึกษา ${sel.year_year}` : "");
    }).catch(() => {});
  }, []);

  const handleSelectYear = (id) => {
    const sel = yearList.find((y) => y.year_id === id);
    if (!sel) return;
    setYearId(id);
    setYearLabel(`ปีการศึกษา ${sel.year_year}`);
    localStorage.setItem("yearId", id);
  };

  // ─── Load periods ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!yearId) return;
    axiosInstance.get(`${API}/getPeriodList/${yearId}`, authHeader())
      .then((res) => setPeriods(res.data.map((p) => ({
        ...p,
        period_begin: p.period_begin?.slice(0, 5),
        period_end: p.period_end?.slice(0, 5),
      }))))
      .catch(() => setPeriods([]));
  }, [yearId]);

  // ─── Load teaching data ───────────────────────────────────────────────────
  useEffect(() => {
    if (!yearId) return;
    setLoading(true);
    axiosInstance.get(`${API}/getTeachingDataByTeacherId/${getUserId()}/${yearId}`, authHeader())
      .then((res) => {
        setTeachingData(res.data);
        if (res.data.length > 0 && res.data[0].t_name) {
          setTeacherName(res.data[0].t_name);
        }
      })
      .catch((err) => {
        if (err.response?.status === 404) setTeachingData([]);
      })
      .finally(() => setLoading(false));
  }, [yearId]);

  // ─── Load guidance periods (คาบแนะแนว — เฉพาะครูที่เป็นครูประจำชั้น) ──────
  useEffect(() => {
    if (!yearId) return;
    axiosInstance.get(`${API}/getGuidanceByAdvisor/${getUserId()}/${yearId}`, authHeader())
      .then((res) => setGuidanceData(res.data || []))
      .catch(() => setGuidanceData([]));
  }, [yearId]);

  // ─── Derived grid — keyed by [day][period_id] ─────────────────────────────
  const grid = React.useMemo(() => {
    const g = {};
    teachingData.forEach((row) => {
      if (row.teachtime_id === null || row.teach_day === null || !row.period_id) return;
      if (String(row.sub_semester) !== String(semester)) return;
      if (!g[row.teach_day]) g[row.teach_day] = {};
      // A teacher may teach multiple subjects in same slot (edge case): keep first
      if (!g[row.teach_day][row.period_id]) {
        g[row.teach_day][row.period_id] = {
          sub_subid: row.sub_subid,
          sub_subname: row.sub_subname,
          sub_type: row.sub_type,
          c_id: row.tb_class_c_id,
          r_no: row.r_no,
        };
      }
    });
    // overlay คาบแนะแนวของเทอมที่เลือก
    guidanceData.forEach((row) => {
      if (String(row.semester) !== String(semester)) return;
      if (!g[row.teach_day]) g[row.teach_day] = {};
      g[row.teach_day][row.period_id] = {
        sub_subid: "แนะแนว",
        sub_subname: "กิจกรรมแนะแนว",
        c_id: row.tb_class_c_id,
        r_no: row.r_no,
        is_guidance: true,
      };
    });
    return g;
  }, [teachingData, guidanceData, semester]);

  // ─── Export PDF ───────────────────────────────────────────────────────────
  const handleExportPDF = async () => {
    if (periods.length === 0) return;

    const periodHeaders = periods.map(
      (p) => `<th style="background:#4472c4;color:#fff;padding:6px 4px;font-size:12px;text-align:center;border:1px solid #ccc;">
                คาบ ${p.period_order}<br/><span style="font-size:10px;font-weight:normal;">${p.period_begin}–${p.period_end}</span>
               </th>`
    ).join("");

    const bodyRows = DAYS.map((d) => {
      const cells = periods.map((p) => {
        const slot = grid[d]?.[p.period_id];
        const content = slot
          ? `<div style="font-weight:600;font-size:11px;">${slot.sub_subid}</div>
             <div style="font-size:10px;color:#333;">${slot.sub_subname}</div>
             <div style="font-size:10px;color:#000;">ม.${slot.c_id}/${slot.r_no}</div>`
          : `<div style="color:#bbb;font-size:10px;text-align:center;">—</div>`;
        const cellBg = slot?.is_guidance ? "#fff3cd" : "#fff";
        return `<td style="padding:4px;border:1px solid #ccc;vertical-align:top;background:${cellBg};">${content}</td>`;
      }).join("");
      return `<tr>
        <td style="padding:6px 8px;font-weight:700;font-size:12px;background:#f2f2f2;border:1px solid #ccc;white-space:nowrap;">${DAY_NAMES[d]}</td>
        ${cells}
      </tr>`;
    }).join("");

    const html = `
      <div style="font-family:Tahoma,Arial,sans-serif;padding:20px;background:#fff;color:#111;">
        <h3 style="margin:0 0 4px;font-size:15px;">ตารางสอน — ${teacherName}</h3>
        <div style="margin-bottom:10px;font-size:12px;color:#555;">${yearLabel} เทอม ${semester}</div>
        <table style="border-collapse:collapse;width:100%;">
          <thead>
            <tr>
              <th style="background:#4472c4;color:#fff;padding:6px 8px;font-size:12px;border:1px solid #ccc;white-space:nowrap;">วัน</th>
              ${periodHeaders}
            </tr>
          </thead>
          <tbody>${bodyRows}</tbody>
        </table>
      </div>`;

    const el = document.createElement("div");
    el.style.cssText = "position:fixed;left:-99999px;top:0;background:#fff;";
    el.innerHTML = html;
    document.body.appendChild(el);
    try {
      const { default: html2canvas } = await import("html2canvas");
      const canvas = await html2canvas(el, { scale: 2 });
      const imgData = canvas.toDataURL("image/png");
      const pw = 841.89; const ph = 595.28;
      const imgW = pw;
      const imgH = (canvas.height * pw) / canvas.width;
      const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
      if (imgH <= ph) {
        doc.addImage(imgData, "PNG", 0, 0, imgW, imgH);
      } else {
        const scale = ph / imgH;
        doc.addImage(imgData, "PNG", 0, 0, imgW * scale, ph);
      }
      doc.save(`ตารางสอน_${teacherName}_เทอม${semester}.pdf`);
    } finally {
      document.body.removeChild(el);
    }
  };

  // ─── Slot cell ────────────────────────────────────────────────────────────
  const SlotCell = ({ slot }) => {
    if (!slot) return (
      <div style={{ minHeight: 58, color: "#ccc", fontSize: "0.7rem", textAlign: "center", paddingTop: 18 }}>—</div>
    );
    const colors = slot.is_guidance
      ? { bg: "#fff3cd", border: "#f0ad4e", text: "#8a6d3b" } // คาบแนะแนว — โทน warning
      : (SEMESTER_COLORS[semester] || SEMESTER_COLORS["1"]);
    return (
      <div style={{
        minHeight: 58, background: colors.bg, border: `1px solid ${colors.border}`,
        borderRadius: 4, padding: "4px 6px", fontSize: "0.72rem",
      }}>
        <div className="fw-semibold" style={{ color: colors.text, lineHeight: 1.2 }}>{slot.sub_subid}</div>
        <div style={{ lineHeight: 1.2, color: "#333" }}>{slot.sub_subname}</div>
        <div style={{ lineHeight: 1.2, color: "#000000", fontSize: "0.8rem" }}>ม.{slot.c_id}/{slot.r_no}</div>
      </div>
    );
  };

  const hasData = teachingData.some(
    (r) => r.teachtime_id !== null && String(r.sub_semester) === String(semester)
  ) || guidanceData.some((g) => String(g.semester) === String(semester));

  return (
    <React.Fragment>
      <Header onSkin={setSkin} />
      <div className="main main-app p-3 p-lg-4">

        {/* Top bar */}
        <div className="d-sm-flex align-items-center justify-content-between mb-4">
          <div>
            <ol className="breadcrumb fs-sm mb-1">
              <li className="breadcrumb-item"><Link to="#">ครูผู้สอน</Link></li>
              <li className="breadcrumb-item active">ตารางสอนของฉัน</li>
            </ol>
            <h4 className="main-title mb-0">ตารางสอนของฉัน</h4>
          </div>
          <div className="d-flex align-items-center gap-2 mt-3 mt-md-0">
            <Dropdown>
              <Dropdown.Toggle variant="primary" id="dropdown-year">
                {yearLabel || "เลือกปีการศึกษา"}
              </Dropdown.Toggle>
              <Dropdown.Menu>
                {yearList.map((y) => (
                  <Dropdown.Item key={y.year_id} onClick={() => handleSelectYear(y.year_id)}>
                    {`ปีการศึกษา ${y.year_year}`}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </div>

        <Row className="g-3">
          <Col xs={12}>
            <Card className="card-one">
              <Card.Header className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                <div>
                  {teacherName && (
                    <span className="fw-semibold">
                      <i className="ri-user-line me-1"></i>{teacherName}
                    </span>
                  )}
                </div>
                <div className="d-flex align-items-center gap-2">
                  <div className="btn-group" role="group">
                    <button
                      type="button"
                      className={`btn btn-sm ${semester === "1" ? "btn-primary" : "btn-outline-primary"}`}
                      onClick={() => setSemester("1")}
                    >
                      เทอม 1
                    </button>
                    <button
                      type="button"
                      className={`btn btn-sm ${semester === "2" ? "btn-primary" : "btn-outline-primary"}`}
                      onClick={() => setSemester("2")}
                    >
                      เทอม 2
                    </button>
                  </div>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    disabled={periods.length === 0 || !hasData}
                    onClick={handleExportPDF}
                  >
                    <i className="ri-file-pdf-line me-1"></i>Export PDF
                  </Button>
                </div>
              </Card.Header>

              <Card.Body className="p-2">
                {periods.length === 0 ? (
                  <div className="text-center text-muted py-5">ยังไม่ได้กำหนดคาบเรียนสำหรับปีนี้</div>
                ) : !hasData ? (
                  <div className="text-center text-muted py-5">
                    <i className="ri-calendar-close-line fs-2 d-block mb-2"></i>
                    ไม่พบตารางสอนในเทอมนี้
                  </div>
                ) : (
                  <div style={{ overflowX: "auto" }}>
                    <table
                      className="table table-bordered mb-0"
                      style={{ tableLayout: "fixed", minWidth: periods.length * 100 + 80 }}
                    >
                      <thead className="table-secondary">
                        <tr>
                          <th style={{ width: 80, fontSize: "0.78rem" }}>วัน</th>
                          {periods.map((p) => (
                            <th key={p.period_id} className="text-center" style={{ fontSize: "0.73rem" }}>
                              <div className="fw-semibold">คาบ {p.period_order}</div>
                              <div className="text-muted fw-normal">{p.period_begin}–{p.period_end}</div>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {DAYS.map((d) => (
                          <tr key={d}>
                            <td className="text-center align-middle fw-semibold" style={{ fontSize: "0.78rem" }}>
                              {DAY_NAMES[d]}
                            </td>
                            {periods.map((p) => (
                              <td key={p.period_id} style={{ padding: 3 }}>
                                <SlotCell slot={grid[d]?.[p.period_id]} />
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Footer />
      </div>
    </React.Fragment>
  );
}
